/**
 * Repeating Field Collection.
 *
 * @format
 */
import React, { useState, useEffect } from 'react';
import FormFields from '../fields';
import Translate from '@components/translation/translate-text-wrapper';
import { FieldErrors } from 'react-hook-form';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Repeating Field Properties.
 */
interface RepeatingFieldProps {
  title: string;
  addButtonText: string;
  removeButtonText: string;
  fieldCollection: any;
  min: number;
  max: number;
  register: any;
  errors?: { [key: string]: FieldErrors };
  repeatingArrayKey: any;
}

/**
 * Repeating Field Collection.
 *
 * @param props
 * @returns
 */
const RepeatingFieldCollection = (
  props: RepeatingFieldProps
): JSX.Element | null => {
  const {
    title,
    addButtonText,
    removeButtonText,
    fieldCollection,
    min,
    max,
    register,
    errors,
    repeatingArrayKey,
  } = props;

  const [fields, setFields] = useState([]);

  /**
   * Add Repeating Field Collection.
   *
   * @param count
   * @returns
   */
  const addRepeatingFieldCollection = (count: number) => {
    const inputFields = [...fields];
    for (let i = 0; i < count; i++) {
      inputFields.push({ value: null });
    }
    return inputFields;
  };

  /**
   * Add Fields.
   */
  const addFields = () => {
    if (fields && max && fields.length < max) {
      const updatedFields = addRepeatingFieldCollection(1);
      setFields(updatedFields);
    }
  };

  /**
   * Remove Fields.
   */
  const removeFields = () => {
    if (fields && min && fields.length > min) {
      const inputFields = [...fields];
      inputFields.pop();
      setFields(inputFields);
    }
  };

  /**
   * Use Effect.
   */
  useEffect(() => {
    if (fieldCollection && min && max) {
      const startingFields = addRepeatingFieldCollection(min);
      setFields(startingFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Render.
   */
  return (
    <div className="repeating-field-container" key={title}>
      <h3>
        <Translate>{title}</Translate>
      </h3>

      {/* Iterate over all repeating fields */}
      {fields.length > 0 &&
        fields.map((field, index) => {
          return (
            <div
              key={`${field}-${index}`}
              className={`repeating-field`}
              data-field-id={index + 1}>
              <FormFields
                fieldList={fieldCollection(index)}
                register={register}
                errors={errors}
                repeatingArrayKey={repeatingArrayKey}
              />
            </div>
          );
        })}

      {fields.length < max && (
        <button className="btn-submit" type="button" onClick={addFields}>
          <Translate>{addButtonText}</Translate>
        </button>
      )}

      {fields.length > min && (
        <button className="btn-submit" type="button" onClick={removeFields}>
          <Translate>{removeButtonText}</Translate>
        </button>
      )}
    </div>
  );
};

export default RepeatingFieldCollection;
